import { MultiSheetMenuBase } from "@/components/ui/multi-sheet-menu/multi-sheet-menu";
import { ToolsTooltipText } from "@/components/ui/tools-toolbar";
import { selectCurrentAreaData } from "@/modes/select-current-area-data";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { FaroTooltip, LayersIcon, ToolButton } from "@faro-lotv/flat-ui";
import { assert, GUID } from "@faro-lotv/foundation";
import {
  isIElementGenericImgSheet,
  isIElementSection,
} from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import { isEqual } from "es-toolkit";
import { useCallback, useMemo, useState, type JSX } from "react";
import { useTagsManagementContext } from "../tags-management-context";
import { selectTagsManagementScene } from "../tags-management-selector";

/** @returns A toolbar button to open the multi-sheet menu */
export function MultiSheetToolButton(): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);
  const { activeAreaId, visibleSheets, setSheetVisibility, setVisibleSheets } =
    useTagsManagementContext();
  const activeArea = useAppSelector(selectIElement(activeAreaId));
  assert(!activeArea || isIElementSection(activeArea), "Invalid active area");

  const areaData = useAppSelector(selectCurrentAreaData(activeArea), isEqual);
  const currentScene = useAppSelector(
    selectTagsManagementScene(areaData),
    isEqual,
  );

  const listVisibleSheets = useMemo(
    () => visibleSheets.map((sheet) => sheet.id),
    [visibleSheets],
  );

  const store = useAppStore();
  const onElementClicked = useCallback(
    (id: GUID) => {
      const sheet = selectIElement(id)(store.getState());
      const visibleSheets = isIElementGenericImgSheet(sheet) ? [sheet] : [];
      setVisibleSheets(visibleSheets);
    },
    [setVisibleSheets, store],
  );

  const onEyeClicked = useCallback(
    (id: GUID, isVisible: boolean) => {
      const sheet = selectIElement(id)(store.getState());
      if (!isIElementGenericImgSheet(sheet)) return;
      setSheetVisibility(sheet, isVisible);
    },
    [setSheetVisibility, store],
  );

  const isSheetVisible = useCallback(
    (sheetId: GUID) => listVisibleSheets.includes(sheetId),
    [listVisibleSheets],
  );

  return (
    <>
      <FaroTooltip placement="left" title={<ToolsTooltipText text="Sheets" />}>
        <ToolButton
          ref={setButtonRef}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          selected={isMenuOpen}
        >
          <LayersIcon />
        </ToolButton>
      </FaroTooltip>
      <MultiSheetMenuBase
        open={isMenuOpen}
        anchorEl={buttonRef}
        onClose={() => setIsMenuOpen(false)}
        activeArea={activeArea}
        activeSheets={visibleSheets}
        availableSheets={currentScene.sheets}
        listVisibleSheets={listVisibleSheets}
        isSheetVisible={isSheetVisible}
        onElementClicked={onElementClicked}
        onEyeClicked={onEyeClicked}
        showEditOptions={false}
      />
    </>
  );
}
